import { useCallback, useEffect, useRef } from 'react';
import { getReviewEnvIdFromHost } from '@hotjar/common';
import type { FeedbackDisplayType, SurveyDisplayType, Widget } from '../constants';

interface WidgetContent {
  initial?: string;
  questions?: Array<{
    text?: string;
  }>;
}

/* eslint-disable camelcase */
export interface PreviewData {
  active_seasonal_emotion_style?: string;
  activeStepInPreview: string | null;
  ask_for_consent: boolean;
  background: string;
  button_color?: string;
  content: WidgetContent;
  display_type?: SurveyDisplayType | FeedbackDisplayType;
  effective_show_branding: boolean;
  emotion_style?: string;
  features: string[];
  id: number;
  language: string;
  legal_name: string | null;
  logo_url?: string;
  pageBackground: string | null;
  parent_element_selector?: string;
  position?: string;
  previewDevice: string;
  privacy_policy_url: string | null;
  show_legal: boolean;
  skin: string;
  widget_label: string | null;
}
/* eslint-enable camelcase */

const widgetReloadFunction = {
  feedback: '_hjFeedbackReload',
  survey: '_hjPollReload',
} as const;

function waitUntil(fn: () => void, conditional: () => boolean) {
  setTimeout(() => {
    if (conditional()) {
      fn();
      return;
    }

    waitUntil(fn, conditional);
  }, 10);
}

interface HjSettings {
  hjid: number;
  preview: boolean;
  host: string;
  staticHost?: string;
  varsHost?: string;
}

export interface HotjarWindow extends Omit<Window, 'hj'> {
  _hjFeedbackReload?: (data: PreviewData, features: string[]) => void;
  _hjPollReload?: (data: PreviewData, features: string[]) => void;
  hj?: unknown;
  _hjSettings?: HjSettings;
}

interface PreviewContentProps {
  data: PreviewData;
  frameWindow: HotjarWindow;
  onReload: () => void;
  shouldReload: boolean;
  widget: Widget;
  frameHeight: number;
}

/* PreviewContent is meant to be rendered within an iframe.
 * Once the client script has loaded, the `reload()` function is called on every render.
 * This is to ensure the preview resets for every `data` change.
 */
export const PreviewContent = ({
  data,
  frameWindow,
  onReload,
  shouldReload,
  widget,
  frameHeight,
}: PreviewContentProps): JSX.Element | null => {
  const scriptLoaded = useRef(false);
  const reloadFunctionName = widgetReloadFunction[widget];
  const reload = useCallback(() => {
    if (typeof frameWindow[reloadFunctionName] === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      frameWindow[reloadFunctionName]!(data, data.features);
      const frameDocument = frameWindow.document.documentElement;
      const height = frameDocument.offsetHeight;
      /* if the iframe content does not take the full size of the iframe, force it to. 
       This fixes a bug in Firefox where the content would be compressed to the min-height 
       of the script otherwise (160px) */
      if (height && height < frameHeight) {
        frameDocument.style.height = '100%';
      }
      onReload();
    }
  }, [data, frameWindow, onReload, reloadFunctionName, frameHeight]);
  const handleScriptLoaded = () => {
    // Wait for client script to initialise
    waitUntil(
      () => {
        scriptLoaded.current = true;
        reload();
      },
      () => typeof frameWindow[reloadFunctionName] !== 'undefined',
    );
  };

  const { previewDevice } = data;

  useEffect(() => {
    if (scriptLoaded.current) {
      reload();
    }
  }, [reload, previewDevice]);

  useEffect(() => {
    if (scriptLoaded.current && shouldReload) {
      reload();
    }
  }, [reload, shouldReload]);

  useEffect(() => {
    const frameDocument = frameWindow.document;
    const { host } = document.location;
    const isLive = host === 'insights.hotjar.com';
    const isStaging = host === 'insights-staging.hotjar.com';
    const reviewEnvId = getReviewEnvIdFromHost();
    const isDev = !isLive && !isStaging && !reviewEnvId;
    const hjSettings: HjSettings = {
      hjid: 0,
      host,
      preview: true,
    };
    let scriptHost;

    // Override the insights server to be localhost for dev environments
    if (isDev) {
      hjSettings.staticHost = 'static.local.hotjar.com:8443';
      hjSettings.varsHost = 'vars.local.hotjar.com:8443';
      scriptHost = 'static.local.hotjar.com:8443';
    } else if (isStaging) {
      scriptHost = 'static-staging.hotjar.com';
    } else if (reviewEnvId) {
      if (document.location.host.includes('webapp-')) {
        scriptHost = 'static-integration.live.eks.hotjar.com';
      } else {
        scriptHost = `static-${reviewEnvId}.review.eks.hotjar.com`;
      }
    } else {
      scriptHost = 'static.hotjar.com';
    }

    /* eslint-disable no-param-reassign */
    frameWindow.hj = {};
    // eslint-disable-next-line no-underscore-dangle
    frameWindow._hjSettings = hjSettings;
    /* eslint-enable no-param-reassign */

    const script = frameDocument.createElement('script');
    script.src = `https://${scriptHost}/c/hotjar-0.js?nocache=1`;
    script.type = 'text/javascript';
    script.onload = handleScriptLoaded;
    frameDocument.head.appendChild(script);

    const embedContainer = frameDocument.createElement('div');
    embedContainer.id = 'hj-preview-container';
    frameDocument.documentElement.style.display = 'flex';
    frameDocument.body.append(embedContainer);
    frameDocument.body.style.margin = 'auto';
    frameDocument.body.style.width = 'fit-content';
    frameDocument.body.style.height = 'fit-content';

    // Use an empty array to ensure this runs only on the first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
